import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Spinner } from '../components/ui/Spinner'

function friendlyError(msg = '') {
  if (msg.toLowerCase().includes('already registered')) return 'An account with that email already exists.'
  if (msg.toLowerCase().includes('password'))           return 'Password must be at least 6 characters.'
  if (msg.toLowerCase().includes('rate limit'))         return 'Too many attempts — please wait a moment and try again.'
  return 'Sign-up failed. Please try again.'
}

export const SignupPage = () => {
  const navigate = useNavigate()

  const [displayName, setDisplayName] = useState('')
  const [email,       setEmail]       = useState('')
  const [password,    setPassword]    = useState('')
  const [error,       setError]       = useState('')
  const [loading,     setLoading]     = useState(false)
  const [done,        setDone]        = useState(false)

  const handleSignup = async (e) => {
    e.preventDefault()
    setError('')
    if (!displayName.trim()) { setError('Please enter your name.'); return }
    setLoading(true)

    const { error: err } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName.trim() } },
    })

    setLoading(false)
    if (err) { setError(friendlyError(err.message)); return }
    setDone(true)
  }

  return (
    <div className="min-h-full flex flex-col items-center justify-center bg-background px-5 py-12">
      <div className="w-full max-w-sm">

        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary mb-4 shadow-card">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">Create your account</h1>
          <p className="text-sm text-text-muted mt-1">Get started with DoorChart</p>
        </div>

        {done ? (
          <div className="text-center space-y-5">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 text-emerald-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-text-secondary">
              Check your inbox — we sent a confirmation link to <strong>{email}</strong>.
            </p>
            <p className="text-xs text-text-muted">
              Once confirmed, sign in and set up your household.
            </p>
            <Button variant="ghost" onClick={() => navigate('/login')}>
              Go to sign in →
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSignup} className="space-y-4">
            <Input
              label="Your name"
              type="text"
              placeholder="e.g. Alex Johnson"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              autoFocus
              required
            />
            <Input
              label="Email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <Input
              label="Password"
              type="password"
              placeholder="At least 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />

            {error && (
              <div className="rounded-base bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" size="lg" disabled={loading}>
              {loading ? <><Spinner size="sm" /> Creating account…</> : 'Create account'}
            </Button>
          </form>
        )}

        <p className="mt-8 text-center text-xs text-text-muted">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
